import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, PageLoader, Text } from "@andrum/ui";
import { cn } from "@/lib/utils";
import {
  loadCoachHistory,
  sendCoachMessage,
} from "@/features/coach/api/coach-api";
import {
  canSendCoachMessage,
  FREE_COACH_MESSAGES_PER_DAY,
  getFreeCoachMessagesRemaining,
  incrementFreeCoachMessage,
} from "@/lib/billing/premium-gate";
import { PremiumGateCard } from "@/components/premium/PremiumGateCard";
import {
  classifySafety,
  crisisResponse,
  elevatedResponse,
} from "@/lib/safety/classify";
import {
  categoryFromCoachIntent,
  coachWhyAppendix,
  wantsExperimentHelp,
} from "@/features/coach/lib/coach-why";
import { t as tStatic } from "@/i18n";
import { CoachThumbs } from "@/features/coach/components/CoachThumbs";

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** false for welcome / crisis / elevated */
  rateable?: boolean;
};

export function CoachScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const suggestions = useMemo(
    () => [
      t("coach.suggestions.pick"),
      t("coach.suggestions.lowEnergy"),
      t("coach.suggestions.week"),
      t("coach.suggestions.stressed"),
      t("coach.suggestions.worked"),
    ],
    [t]
  );

  const [messages, setMessages] = useState<Message[]>(() => [
    {
      id: "welcome",
      role: "assistant",
      content: tStatic("coach.welcome"),
      rateable: false,
    },
  ]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [loaded, setLoaded] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [remaining, setRemaining] = useState(FREE_COACH_MESSAGES_PER_DAY);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    void (async () => {
      const history = await loadCoachHistory();
      if (history.length > 0) {
        setMessages(
          history.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            rateable: m.role === "assistant",
          }))
        );
        setConversationId(history[history.length - 1]?.conversationId);
      }
      const gate = await canSendCoachMessage();
      setIsPremium(gate.isPremium);
      setRemaining(
        gate.isPremium ? FREE_COACH_MESSAGES_PER_DAY : gate.remaining
      );
      setGateOpen(!gate.ok);
      setLoaded(true);
    })();
  }, []);

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;

    void (async () => {
      const gate = await canSendCoachMessage();
      if (!gate.ok) {
        setGateOpen(true);
        setRemaining(0);
        setIsPremium(false);
        return;
      }

      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
      };
      const next = [...messages, userMsg];
      setMessages(next);
      setInput("");
      setIsThinking(true);

      // Local safety first, never wait on network for crisis language
      const safety = classifySafety(trimmed);
      if (safety.level === "crisis" || safety.level === "elevated") {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content:
              safety.level === "crisis"
                ? crisisResponse()
                : elevatedResponse(),
            rateable: false,
          },
        ]);
        // Do not count crisis/elevated toward free quota
        setIsThinking(false);
        return;
      }

      const lower = trimmed.toLowerCase();
      const wantsWeek =
        lower.includes("sammanfatta") ||
        lower.includes("vecka") ||
        lower.includes("fungerat för mig") ||
        lower.includes("summarise") ||
        lower.includes("summarize") ||
        lower.includes("week") ||
        lower.includes("worked for me");

      if (wantsWeek) {
        const { fetchWeeklySummary } = await import(
          "@/features/insights/api/weekly-api"
        );
        const weekly = await fetchWeeklySummary();
        if (weekly.ok) {
          const parts = [weekly.data.summary];
          if (weekly.data.suggestion) {
            parts.push(
              `\n\n${t("coach.nextStep", { text: weekly.data.suggestion })}`
            );
          }
          const why = coachWhyAppendix({ category: "presence" });
          parts.push(
            why.replace(
              /Varför den här typen av steg:|Why this kind of step:/i,
              t("coach.patternsPrefix").replace(/:$/, "") + ":"
            )
          );
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: parts.join(""),
            },
          ]);
          if (!gate.isPremium) {
            incrementFreeCoachMessage();
            setRemaining(getFreeCoachMessagesRemaining());
          }
          setIsThinking(false);
          return;
        }
      }

      const result = await sendCoachMessage(
        next.map((m) => ({ role: m.role, content: m.content })),
        conversationId
      );
      if (result.ok && result.conversationId) {
        setConversationId(result.conversationId);
      }
      let content = result.content;
      if (wantsExperimentHelp(trimmed)) {
        const cat = categoryFromCoachIntent(trimmed);
        content = `${content}${coachWhyAppendix(
          cat ? { category: cat } : {}
        )}`;
      }
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content,
        },
      ]);
      if (!gate.isPremium) {
        incrementFreeCoachMessage();
        const left = getFreeCoachMessagesRemaining();
        setRemaining(left);
        if (left <= 0) setGateOpen(true);
      }
      setIsThinking(false);
    })();
  };

  // Keep recent messages in a scrollable pane so input stays above bottom nav
  const visibleMessages = messages.slice(-12);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="shrink-0 space-y-1 px-5 pt-1 pb-2">
        <Text variant="title" as="h1">
          {t("coach.title")}
        </Text>
        <Text variant="muted" className="line-clamp-1 text-sm leading-snug">
          {t("coach.lead")}
        </Text>
        {!isPremium && loaded ? (
          <Text variant="caption">
            {t("coach.freeQuota", {
              remaining,
              total: FREE_COACH_MESSAGES_PER_DAY,
            })}
          </Text>
        ) : null}
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-x-hidden overflow-y-auto overscroll-y-contain px-5 py-2 [-webkit-overflow-scrolling:touch]">
        {!loaded ? (
          <PageLoader
            fullHeight={false}
            className="min-h-[24vh]"
            label={t("coach.loadingHistory")}
          />
        ) : null}

        {loaded
          ? visibleMessages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug",
                  msg.role === "user"
                    ? "ml-auto bg-primary-soft text-foreground"
                    : "mr-auto bg-surface-raised text-foreground shadow-sm"
                )}
              >
                {msg.content.split("\n").slice(0, 8).map((line, i) => (
                  <p key={i} className={i > 0 ? "mt-1.5" : undefined}>
                    {line}
                  </p>
                ))}
                {msg.role === "assistant" ? (
                  <CoachThumbs
                    messageId={msg.id}
                    disabled={msg.rateable === false}
                  />
                ) : null}
              </div>
            ))
          : null}

        {isThinking ? (
          <div className="mr-auto rounded-2xl bg-surface-raised px-3.5 py-2.5 text-sm text-foreground-muted shadow-sm">
            <span className="inline-block animate-pulse">
              {t("coach.thinking")}
            </span>
          </div>
        ) : null}

        {gateOpen ? (
          <PremiumGateCard
            title={t("coach.gateTitle")}
            description={t("coach.gateBody")}
            reason="coach"
          />
        ) : null}
      </div>

      {messages.length <= 2 && !gateOpen ? (
        <div className="flex shrink-0 flex-wrap gap-1.5 px-5 pb-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              className="rounded-full border border-border bg-surface-raised px-3 py-1.5 text-xs font-medium text-foreground-muted"
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      <div className="shrink-0 border-t border-border/80 bg-surface-raised/95 px-4 pt-2.5 pb-2 backdrop-blur-md">
        {gateOpen ? (
          <Button
            fullWidth
            variant="soft"
            onClick={() =>
              void navigate({
                to: "/app/paywall",
                search: { reason: "coach" },
              })
            }
          >
            {t("coach.openPlus")}
          </Button>
        ) : (
          <form
            className="flex items-end gap-2.5"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("coach.placeholder")}
              className="min-h-12 flex-1 rounded-2xl border border-input bg-surface-raised px-4 py-3 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("coach.messageLabel")}
            />
            <Button type="submit" disabled={isThinking || !input.trim()}>
              {t("coach.send")}
            </Button>
          </form>
        )}
        <Text variant="caption" className="mt-2.5 text-center">
          {t("coach.footer")}
        </Text>
      </div>
    </div>
  );
}
